import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react';
import { bff } from '@/lib/api/client';
import { useErrorStore } from '@/store/useErrorStore';

/**
 * Chat attachments (Backlog N5): a file for one conversation, not the library.
 *
 * On send, the BFF signs a PUT into the user's chat/ folder in SREG's bucket,
 * the browser uploads the file there directly, and the message carries the
 * gs:// URI, which Gemini reads inside SREG's project. A text label goes with
 * it so Simon can name the file when he cites it.
 */

/** What Gemini reads directly. The BFF (server/bff/attachments.py) keeps the same list. */
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  txt: 'text/plain',
  csv: 'text/csv',
};
export const ATTACHMENT_ACCEPT = Object.keys(MIME_BY_EXTENSION)
  .map((e) => `.${e}`)
  .join(',');
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;
export const ATTACHMENT_HINT =
  'Simon reads PDF, image (PNG, JPEG, WebP), text and CSV attachments up to 50 MB. Save Word or Excel files as PDF first.';

export function attachmentMime(name: string): string | undefined {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  return name.includes('.') ? MIME_BY_EXTENSION[ext] : undefined;
}

/** The label sent with the file, and how the message shows it after a reload. */
export const attachedLabel = (name: string) => `[Attached: ${name}]`;

interface AttachmentTicket {
  fileUri: string;
  mimeType: string;
  upload: { method: 'PUT'; url: string; headers: Record<string, string> };
}

function refuse(message: string): never {
  useErrorStore.getState().show({ message });
  throw new Error(message);
}

export const simonAttachments: AttachmentAdapter = {
  accept: ATTACHMENT_ACCEPT,

  async add({ file }): Promise<PendingAttachment> {
    const mime = attachmentMime(file.name);
    if (!mime) refuse(`${file.name}: ${ATTACHMENT_HINT}`);
    if (file.size > MAX_ATTACHMENT_BYTES) refuse(`${file.name} is over 50 MB.`);
    return {
      id: crypto.randomUUID(),
      type: mime.startsWith('image/') ? 'image' : 'document',
      name: file.name,
      contentType: mime,
      file,
      status: { type: 'requires-action', reason: 'composer-send' },
    };
  },

  async send(attachment, options): Promise<CompleteAttachment> {
    const ticket = await bff.post<AttachmentTicket>('/attachments', {
      filename: attachment.name,
      size: attachment.file.size,
    });
    const r = await fetch(ticket.upload.url, {
      method: 'PUT',
      headers: ticket.upload.headers,
      body: attachment.file,
      signal: options?.signal,
    });
    if (!r.ok) refuse(`${attachment.name} could not be uploaded (${r.status}).`);
    return {
      ...attachment,
      status: { type: 'complete' },
      content: [
        { type: 'text', text: attachedLabel(attachment.name) },
        { type: 'file', data: ticket.fileUri, mimeType: ticket.mimeType, filename: attachment.name, sourceType: 'url' },
      ],
    };
  },

  async remove() {
    // Nothing was uploaded before send.
  },
};
